/* eslint-disable camelcase */
import * as React from 'react'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { useLocale, useTranslation } from 'hooks'
import {
  csrfToken,
  withTranslations,
  formatServerError,
  withGrowthbook
} from 'helpers'
import Checkbox from '../atoms/Checkbox'
import PasswordForm from '../passwords/PasswordForm'
import InputField from '../atoms/InputField/InputField'
/**
 * Renders the registration form component.
 *
 * @param {RegistrationFormProps} RegistrationFormProps - The component props.
 * @returns {JSX.Element} - The rendered component.
 */
const RegistrationForm = ({
  resource,
  errors,
  guest_checkout_path = '/checkout/guest',
  apple_omniauth_path = '/users/auth/apple',
  google_omniauth_path = '/users/auth/google_oauth2',
  cart_items = [],
  params
}) => {
  const [firstName, setFirstName] = React.useState(resource.first_name)
  const [firstNameError, setFirstNameError] = React.useState(errors.first_name)
  const [lastName, setLastName] = React.useState(resource.last_name)
  const [lastNameError, setLastNameError] = React.useState(errors.last_name)
  const [email, setEmail] = React.useState(resource.email)
  const [emailError, setEmailError] = React.useState(errors.email)
  const [email2, setEmail2] = React.useState(resource.email_confirmation)
  const [emailError2, setEmailError2] = React.useState(
    errors.email_confirmation
  )
  const [isEmail2Focused, setIsEmail2Focused] = React.useState(false)
  const [hasEmailBlurredOnce2, setHasEmailBlurredOnce2] = React.useState(false)
  const [marketingConsent, setMarketingConsent] = React.useState(false)

  const { t } = useTranslation()
  const { localiseUrl } = useLocale()

  const signInWithApple = useFeatureIsOn('sign_in_with_apple')
  const signInWithGoogle = useFeatureIsOn('sign_in_with_google')

  return (
    <div className="form-group">
      {(signInWithApple || signInWithGoogle) && (
        <>
          <div className={`button-group button-group--large-margin`}>
            {signInWithApple && (
              <a
                href={localiseUrl(apple_omniauth_path)}
                className="button-group__item button-group__item--equal-width btn btn--secondary"
                data-method="post"
              >
                <img
                  className="btn__icon btn__icon--left"
                  src="https://assets.laced.com/laced_app_assets/images/apple-logo.svg"
                  alt={t('shared.sign_up_with', { val: 'Apple' })}
                  width={16}
                />
                {t('shared.sign_up_with', { val: 'Apple' })}
              </a>
            )}
            {signInWithGoogle && (
              <a
                href={localiseUrl(google_omniauth_path)}
                className="button-group__item button-group__item--equal-width btn btn--secondary"
                data-method="post"
              >
                <img
                  className="btn__icon btn__icon--left"
                  src="https://assets.laced.com/laced_app_assets/images/google-logo.svg"
                  alt={t('shared.sign_up_with', { val: 'Google' })}
                  width={16}
                />
                {t('shared.sign_up_with', { val: 'Google' })}
              </a>
            )}
          </div>

          <hr />
        </>
      )}

      <form
        id="new_user"
        action={localiseUrl('/users', { localeAsParam: true })}
        method="post"
        className="new_user"
        acceptCharset="UTF-8"
      >
        <input type="hidden" value="✓" name="utf8" />
        <input type="hidden" value={csrfToken} name="authenticity_token" />

        <div className="field">
          <InputField
            labelText={t('shared.first_name')}
            id="First name"
            name="user[first_name]"
            type="text"
            value={firstName || ''}
            onChange={(e) => {
              setFirstNameError(null)
              setFirstName(e.target.value)
            }}
            className={firstNameError ? 'error' : ''}
          />

          <span className="error-messages footnote">
            {formatServerError(
              firstNameError,
              t('react_components.register.please_enter', {
                field: t('shared.first_name')
              })
            )}
          </span>
        </div>

        <div className="field">
          <InputField
            labelText={t('shared.last_name')}
            id="Last name"
            name="user[last_name]"
            type="text"
            value={lastName || ''}
            onChange={(e) => {
              setLastNameError(null)
              setLastName(e.target.value)
            }}
            className={lastNameError ? 'error' : ''}
          />

          <span className="error-messages footnote">
            {formatServerError(
              lastNameError,
              t('react_components.register.please_enter', {
                field: t('shared.last_name')
              })
            )}
          </span>
        </div>

        <div className="field">
          <InputField
            labelText={t('shared.email')}
            id="Email"
            name="user[email]"
            type="email"
            value={email || ''}
            onChange={(e) => {
              setEmailError(null)
              setEmail(e.target.value)
            }}
            autoComplete="email"
            className={emailError ? 'error' : ''}
          />

          <span className="error-messages footnote">
            {formatServerError(
              emailError,
              t('react_components.register.please_enter', {
                field: t('shared.email')
              })
            )}
          </span>
        </div>

        <div className="field">
          <InputField
            labelText={t('react_components.register.email_re')}
            id="Email confirmation"
            type="email"
            name="user[email_confirmation]"
            value={email2 || ''}
            onChange={(e) => {
              setEmailError2(null)
              setEmail2(e.target.value)
            }}
            onBlur={() => {
              setIsEmail2Focused(false)
              setHasEmailBlurredOnce2(true)
            }}
            onFocus={() => {
              setIsEmail2Focused(true)
            }}
            className={`
                ${hasEmailBlurredOnce2 && email !== email2 ? 'error' : ''}
                ${emailError2 ? 'error' : ''}`}
          />

          {emailError2 ? (
            <span className="error-messages footnote">{emailError2}</span>
          ) : (
            hasEmailBlurredOnce2 &&
            email !== email2 &&
            !isEmail2Focused && (
              <span className="error-messages footnote">
                {t('react_components.register.email_dont_match')}
              </span>
            )
          )}
        </div>

        <PasswordForm
          errorMessage={errors.password && errors.password[0]}
          errorMessage2={
            errors.password_confirmation && errors.password_confirmation[0]
          }
        />

        <Checkbox
          name="user[marketing_consent]"
          id="Marketing consent"
          value={marketingConsent}
          inverted={false}
          onClick={() => {
            setMarketingConsent(!marketingConsent)
          }}
        >
          {t('shared.marketing_consent')}
        </Checkbox>

        <div
          className={`button-group button-group--large-margin ${
            cart_items.length > 0 ? 'button-group--end' : ''
          }`}
        >
          {cart_items.length > 0 && (
            <a
              href={localiseUrl(guest_checkout_path)}
              className="button-group__item btn btn--secondary"
            >
              {t('shared.guest_checkout')}
            </a>
          )}
          <input
            type="submit"
            name="commit"
            value={t('shared.sign_up')}
            className="button-group__item btn btn--primary"
            data-disable-with="Sign up"
          />
        </div>

        <span
          className="form-bottom-text footnote"
          dangerouslySetInnerHTML={{
            __html: t('react_components.register.agreement', {
              escapeValue: false
            })
          }}
        />
      </form>
    </div>
  )
}

/**
 * @typedef {{
 * resource: { first_name: string,last_name: string, email: string, email_confirmation: string },
 * errors: { first_name: string | null,last_name: string | null, email: string | null, email_confirmation: string | null, password: string | undefined, password_confirmation: string | undefined },
 * guest_checkout_path?: string,
 * apple_omniauth_path?: string,
 * google_omniauth_path?: string,
 * cart_items?: Array<object>,
 * params: {action: string, controller: string, code: string, src: string},
 * }} RegistrationFormProps
 */

export default withGrowthbook(withTranslations(RegistrationForm))
